import { ChangeDetectionStrategy, Component, inject, Input, OnInit } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatDialog } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { firstValueFrom, map, Observable } from 'rxjs'
import { AvatarComponent } from '../../avatar/avatar.component'
import { Meeting, MeetingsService } from '../../meetings.service'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { ToastService } from '../../toast.service'
import { UserNamePipe } from '../../user-name.pipe'
import { User, UsersService } from '../../users.service'
import { constructNameForUser } from '../../utils'

@Component({
  selector: 'app-meeting-owners-card',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatAutocompleteModule,
    MatButton,
    MatIconButton,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatTooltip,
    TranslatePipe,
    AvatarComponent,
    UserNamePipe,
  ],
  templateUrl: './meeting-owners-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-owners-card.component.scss',
})
export class MeetingOwnersCardComponent implements OnInit {
  @Input({ required: true })
  meeting!: Meeting

  private meetingsService = inject(MeetingsService)
  private usersService = inject(UsersService)
  private toastService = inject(ToastService)
  private translateService = inject(TranslateService)
  private dialog = inject(MatDialog)

  addMeetingOwnerMutation = this.meetingsService.addMeetingOwnerMutation()
  removeMeetingOwnerMutation = this.meetingsService.removeMeetingOwnerMutation()

  currentUserId = this.usersService.currentUser$.value?.id ?? null

  /** The autocomplete's text, which matches one option exactly once a user has been picked. */
  filter = ''

  teamUsers$!: Observable<User[]>

  ngOnInit() {
    // A support user's own team has nothing to do with the meeting, so they are offered the
    // people of the meeting's team instead.
    this.teamUsers$ =
      this.usersService.currentUser$.value?.role === 'SUPPORT'
        ? this.usersService.getUsersByTeamId(this.meeting.teamId).pipe(map((result) => result.data?.list ?? []))
        : this.usersService.getTeamUsers().pipe(map((result) => result.data ?? []))
  }

  get canManage(): boolean {
    return this.meeting.accessLevel === 'FULL_ACCESS'
  }

  get isLastMeetingOwner(): boolean {
    return this.meeting.users.length === 1
  }

  addableUsers(teamUsers: User[]): User[] {
    const filter = this.filter.toLowerCase()
    return teamUsers
      .filter((user) => !this.meeting.users.some((meetingOwner) => meetingOwner.id === user.id))
      .filter((user) => filter === '' || this.describeUser(user).toLowerCase().includes(filter))
  }

  selectedUser(teamUsers: User[]): User | null {
    return this.addableUsers(teamUsers).find((user) => this.describeUser(user) === this.filter) ?? null
  }

  // Doubles as the option's value, so that the picked option is recognisable from the text alone.
  describeUser(user: User): string {
    return `${constructNameForUser(user)} (${user.email})`
  }

  async addMeetingOwner(user: User | null) {
    if (user === null) {
      return
    }
    try {
      await this.addMeetingOwnerMutation.mutateAsync({ meetingId: this.meeting.id, userId: user.id })
      this.filter = ''
      this.toastService.show('MEETING_SETUP.MEETING_OWNERS.ADDED')
    } catch {
      this.toastService.show('MEETING_SETUP.MEETING_OWNERS.ADD_ERROR')
    }
  }

  async removeMeetingOwner(user: User) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('MEETING_SETUP.MEETING_OWNERS.REMOVE_DIALOG.TITLE'),
        description: this.translateService.instant('MEETING_SETUP.MEETING_OWNERS.REMOVE_DIALOG.DESCRIPTION', { userName: constructNameForUser(user) }),
      },
    })
    const confirmed = await firstValueFrom(dialogRef.afterClosed())
    if (confirmed !== true) {
      return
    }
    try {
      await this.removeMeetingOwnerMutation.mutateAsync({ meetingId: this.meeting.id, userId: user.id })
      this.toastService.show('MEETING_SETUP.MEETING_OWNERS.REMOVED')
    } catch {
      this.toastService.show('MEETING_SETUP.MEETING_OWNERS.REMOVE_ERROR')
    }
  }
}
